// This mixin contains code that is used for only electricity half hourly advances
import moment from "moment";
import { _ } from "underscore";

export default {
  data() {
    return {
      selectedDay: moment()
        .subtract(1, "day"),
      selectedYear: moment().format("YYYY"),
      lastMonthInShownRange: null,
    };
  },
  created() {
    this.lastMonthInShownRange = moment().startOf("month");
    this.updateShownRange();
  },
  computed: {
    activeService() {
      return this.$store.state.meters.activeService;
    },
    shouldShowAdvances() {
      // Use definitive logic: customer has ever been HH AND advance ranges are available
      return this.$store.getters.hasEverBeenHH && 
             this.$store.state.meters.advanceRangesAvailable === true;
    },
    // Provide backward compatibility for components that still expect HhMeterPoint props
    HhMeterPoint() {
      return this.shouldShowAdvances ? {
        AdvancesUntil: this.advancesUntil
      } : null;
    },
    advancesUntil() {
      // Calculate AdvancesUntil from the advance ranges
      const ranges = this.$store.state.meters.advanceRAGRanges;
      if (!ranges || ranges.length === 0) return null;
      
      // Find the latest end date from all advance ranges
      const latestEndDate = ranges.reduce((latest, range) => {
        const endDate = moment(range.RangeEnd);
        return !latest || endDate.isAfter(latest) ? endDate : latest;
      }, null);
      
      return latestEndDate ? latestEndDate.format() : null;
    },

    supplyStartDate() {
      // First try to get from Registration data
      const getMinDate = (a, b) => (moment(a).isBefore(b) ? a : b);
      const getSupplyStartDate = (supplyStartDate, { SupplyStartDate }) =>
        getMinDate(supplyStartDate, SupplyStartDate);
      
      const registrationStartDate = this.registrations.reduce(getSupplyStartDate, undefined);
      
      // If no registration start date, fall back to advance ranges
      if (registrationStartDate) {
        return registrationStartDate;
      }
      
      // Use advance ranges to determine the earliest valid date
      const ranges = this.$store.state.meters.advanceRAGRanges;
      if (ranges && ranges.length > 0) {
        const earliestStartDate = ranges.reduce((earliest, range) => {
          const startDate = moment(range.FromDate);
          return !earliest || startDate.isBefore(earliest) ? startDate : earliest;
        }, null);
        return earliestStartDate ? earliestStartDate.toDate() : undefined;
      }
      
      return undefined;
    },

    registrations() {
      // Use Registration data from FlowBot instead of AdvancesBot HhMeterPoint
      const registration = this.$store.state.meters.activeService.Registration;
      return registration ? [registration] : [];
    },

    years() {
      let years = [];
      let activeYear = moment().format("YYYY");
      while (activeYear >= 2005) {
        years.push(activeYear.toString());
        activeYear -= 1;
      }
      return years;
    },

    lastSixMonths() {
      let lastSixMonths = [];
      let activeMonth = this.lastMonthInShownRange.format();
      lastSixMonths.push(activeMonth);

      for (let i = 0; i < 5; i++) {
        activeMonth = moment(activeMonth).subtract(1, "months");
        lastSixMonths.push(activeMonth.format());
      }
      return lastSixMonths.reverse();
    },
    disableButton() {
      // Returns a bool for if the next 6 months button should be disabled or not
      let currentDate = moment()
        .startOf("month")
        .format("YYYY, MM, DD");
      return this.lastMonthInShownRange.isSameOrAfter(currentDate);
    },

  },
  methods: {
    isInSupplyTimeRange(date) {
      // Get the valid date range from advance ranges if available
      const ranges = this.$store.state.meters.advanceRAGRanges;
      if (ranges && ranges.length > 0) {
        // Check if the date falls within any of the advance ranges
        return ranges.some(range => {
          return moment(date).isBetween(
            moment(range.FromDate),
            moment(range.ToDate),
            "days",
            "[]" // Include both start and end dates
          );
        });
      }
      
      // Fallback to original logic if no advance ranges
      return moment(date).isBetween(
        this.supplyStartDate,
        moment(),
        "days",
        "[)"
      );
    },
    hasNoAdvances(date) {
      // Use QueenBee advance ranges to determine if advances are available for a date
      const advanceRanges = this.$store.state.meters.advanceRAGRanges || [];
      if (advanceRanges.length === 0) {
        return true; // No advance ranges available
      }
      
      // Find the latest date in green (available) ranges
      const greenRanges = advanceRanges.filter(range => range.Colour === 'Green');
      if (greenRanges.length === 0) {
        return true; // No green ranges available
      }
      
      // Get the latest ToDate from green ranges
      const latestGreenDate = Math.max(...greenRanges.map(range => 
        new Date(range.ToDate).getTime()
      ));
      
      return new Date(date).getTime() > latestGreenDate;
    },
    updateShownRange() {
      this.lastMonthInShownRange = moment(this.selectedYear + "-6-1");
    },

    showAdvances(date) {
      if (this.isInSupplyTimeRange(date)) {
        if (!this.hasLoadedAdvancesFor(date)) {
          this.getAdvances(date);
        }
      }
    },

    getAdvances(date) {
      // Use Mpan from MeterPoint instead of HhMeterPoint
      const meterPoint = this.$store.state.meters.activeService.MeterPoint;
      const mpan = meterPoint && meterPoint.Mpan;
      if (mpan) {
        this.$store.dispatch("getAdvancesForDate", {
          serviceIdentifier: mpan,
          date: moment(date).format("YYYY-MM-DD"),
        });
      }
    },

    // Checks if advances for a date have already been loaded from the api
    hasLoadedAdvancesFor(date) {
      if (
        _.find(this.$store.state.meters.advances, function(advance) {
          return moment(date).isSame(advance.Date, "day");
        })
      )
        return true;
      return false;
    },

    showPrevSixMonths() {
      this.lastMonthInShownRange = this.lastMonthInShownRange.subtract(
        6,
        "months"
      );
      this.selectedYear = this.lastMonthInShownRange.format("YYYY");
    },

    showNextSixMonths() {
      let currentDate = moment().startOf("month");

      if (this.lastMonthInShownRange.isBefore(currentDate)) {
        this.lastMonthInShownRange = this.lastMonthInShownRange.add(6, "month");
        this.selectedYear = this.lastMonthInShownRange.format("YYYY");
      }
    },
  },
};
